/**
 * UPT-2124 J2d (spec `web/SPEC-UPT-2124-J2d.md` §2 n° 2) — « la voix agit » : la FILE DE CONFIRMATION et l'outil générique `confirm`.
 *
 * Un outil créateur (ex. `create_dossier`) ne fait rien tout de suite : il affiche une carte de ce qui va être fait, pose la question au modèle
 * et DEMANDE une confirmation ici (`requestVoiceConfirmation`). Une seule confirmation en attente à la fois (une nouvelle remplace la précédente,
 * qui expire) ; péremption 60 s (`VOICE_CONFIRM_TTL_MS`) : sans réponse, `onExpire` (la carte « Proposition expirée »), rien n'est écrit.
 * La réponse vient de la VOIX — le modèle appelle l'outil générique `confirm {decision: 'yes' | 'no'}` (déclaré au modèle comme les autres outils
 * du registre, J2a `session.update`) — ou d'un CLIC sur la carte (« Créer » / « Annuler ») : les deux passent par `decideVoiceConfirmation`.
 *
 * Mémoire seule, aucune lecture réseau, aucun stockage. L'enregistrement de `confirm` dans le registre est compté (`registerConfirmTool`) :
 * chaque page qui enregistre un outil à confirmation le demande, le dernier retrait le retire.
 */
import { registerVoiceTool, type VoiceToolOutput } from './voiceToolRegistry';
import type { VoiceToolDefinition } from './voiceTypes';

/** Péremption d'une confirmation en attente : 60 s. */
export const VOICE_CONFIRM_TTL_MS = 60_000;

/** Nom de l'outil générique (le modèle vocal l'appelle quand il a entendu « oui » / « non » ; le simulateur aussi). */
export const CONFIRM_TOOL_NAME = 'confirm';

export type VoiceConfirmDecision = 'yes' | 'no';

/** Définition envoyée au modèle vocal. */
export const CONFIRM_TOOL: VoiceToolDefinition = {
  type: 'function',
  name: CONFIRM_TOOL_NAME,
  description:
    "Transmet la réponse de l'utilisateur à la question de confirmation en attente (« oui » = faire, « non » = ne rien faire). À appeler seulement après avoir posé la question et entendu la réponse.",
  parameters: {
    type: 'object',
    properties: { decision: { type: 'string', enum: ['yes', 'no'], description: 'La réponse entendue.' } },
    required: ['decision'],
    additionalProperties: false,
  },
};

export interface VoiceConfirmationRequest {
  /** L'outil qui pose la question (ex. `create_dossier`). */
  tool: string;
  /** Joué sur « oui » / « non » ; rend la sortie renvoyée au modèle (le texte dit). */
  onDecision: (decision: VoiceConfirmDecision) => Promise<VoiceToolOutput> | VoiceToolOutput;
  /** Joué quand la confirmation expire (60 s sans réponse) ou qu'une autre la remplace : rien n'est écrit. */
  onExpire?: () => void;
}

export interface VoiceConfirmation extends VoiceConfirmationRequest {
  id: string;
  createdAt: number;
  expiresAt: number;
}

type Listener = () => void;

let pending: VoiceConfirmation | null = null;
let timer: ReturnType<typeof setTimeout> | null = null;
let sequence = 0;
const listeners = new Set<Listener>();

const notify = (): void => {
  listeners.forEach((listener) => listener());
};

const clearTimer = (): void => {
  if (timer !== null) {
    clearTimeout(timer);
    timer = null;
  }
};

/** Retire la confirmation en attente sans rien jouer ; rend celle qui attendait. */
const take = (): VoiceConfirmation | null => {
  const current = pending;
  pending = null;
  clearTimer();
  if (current) {
    notify();
  }
  return current;
};

/** Fait expirer la confirmation en attente (péremption ou remplacement) : `onExpire`, rien n'est écrit. */
export const expireVoiceConfirmation = (): void => {
  const current = take();
  current?.onExpire?.();
};

/**
 * Demande une confirmation : remplace (fait expirer) celle qui attendait, arme la péremption, prévient les abonnés.
 * `now` et `ttlMs` sont exposés pour les tests.
 */
export const requestVoiceConfirmation = (request: VoiceConfirmationRequest, now: number = Date.now(), ttlMs: number = VOICE_CONFIRM_TTL_MS): VoiceConfirmation => {
  expireVoiceConfirmation();
  sequence += 1;
  const confirmation: VoiceConfirmation = { ...request, id: `confirm-${sequence}`, createdAt: now, expiresAt: now + ttlMs };
  pending = confirmation;
  timer = setTimeout(() => {
    if (pending === confirmation) {
      expireVoiceConfirmation();
    }
  }, ttlMs);
  notify();
  return confirmation;
};

/** La confirmation en attente, ou `null` (une confirmation périmée est expirée au passage). */
export const pendingVoiceConfirmation = (now: number = Date.now()): VoiceConfirmation | null => {
  if (pending && pending.expiresAt <= now) {
    expireVoiceConfirmation();
  }
  return pending;
};

/** Identifiant de la confirmation en attente (instantané pour `useSyncExternalStore`), `null` sinon. */
export const pendingVoiceConfirmationId = (): string | null => pending?.id ?? null;

export const hasPendingVoiceConfirmation = (now: number = Date.now()): boolean => pendingVoiceConfirmation(now) !== null;

/**
 * Tranche la confirmation en attente (voix ou clic) : elle est retirée AVANT que `onDecision` ne joue (l'outil peut en redemander une, ex. code pris
 * → nouvelle proposition). Rend la sortie de l'outil, ou `null` quand rien n'attend (ou que `id` n'est pas celle qui attend).
 */
export const decideVoiceConfirmation = async (decision: VoiceConfirmDecision, id?: string, now: number = Date.now()): Promise<VoiceToolOutput | null> => {
  const current = pendingVoiceConfirmation(now);
  if (!current || (id !== undefined && current.id !== id)) {
    return null;
  }
  take();
  return current.onDecision(decision);
};

export const subscribeVoiceConfirm = (listener: Listener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const isDecision = (value: unknown): value is VoiceConfirmDecision => value === 'yes' || value === 'no';

/** Exécution de l'outil `confirm` : la décision va à la confirmation en attente ; sans elle, `nothing_pending` ; décision illisible, `invalid_decision`. */
export const executeConfirmTool = async (args: Record<string, unknown>): Promise<VoiceToolOutput> => {
  const decision = args.decision;
  if (!isDecision(decision)) {
    return { outputJson: JSON.stringify({ error: 'invalid_decision' }) };
  }
  const result = await decideVoiceConfirmation(decision);
  return result ?? { outputJson: JSON.stringify({ error: 'nothing_pending' }) };
};

let registrations = 0;
let unregisterConfirm: (() => void) | null = null;

/** Enregistre l'outil `confirm` dans le registre (compté) ; rend le retrait. */
export const registerConfirmTool = (): (() => void) => {
  registrations += 1;
  if (registrations === 1) {
    unregisterConfirm = registerVoiceTool({ definition: CONFIRM_TOOL, execute: executeConfirmTool });
  }
  let released = false;
  return () => {
    if (released) {
      return;
    }
    released = true;
    registrations = Math.max(0, registrations - 1);
    if (registrations === 0) {
      unregisterConfirm?.();
      unregisterConfirm = null;
    }
  };
};

/** Remise à zéro (tests) : la confirmation en attente est oubliée sans rien jouer, l'enregistrement compté aussi. */
export const resetVoiceConfirm = (): void => {
  take();
  registrations = 0;
  unregisterConfirm?.();
  unregisterConfirm = null;
};
